import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Loader2, Lock, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/Markdown";
import { DiscussionBody, LiveBody, ProjectBody, QuizBody, ResourceBody, VideoBody } from "@/components/learner/LessonBodies";
import { FORMAT_LABELS, errMsg, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

function Notes({ lessonId, initial }) {
  const api = useApi();
  const [text, setText] = useState(initial);
  const [state, setState] = useState("saved");
  const timer = useRef(null);
  const change = (v) => {
    setText(v);
    setState("typing");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { await api.put(`/lessons/${lessonId}/note`, { text: v }); setState("saved"); } catch { setState("error"); }
    }, 800);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><NotebookPen className="h-4 w-4 text-[#222d99]" /> My notes</p>
        <span className="text-xs text-slate-400" data-testid="notes-save-state">{state === "saved" ? "Saved" : state === "error" ? "Not saved" : "Saving…"}</span>
      </div>
      <Textarea value={text} onChange={(e) => change(e.target.value)} rows={7} maxLength={10000} placeholder="Private notes for this lesson" data-testid="lesson-notes-input" />
    </div>
  );
}

function Outline({ outline, current, done }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="lesson-outline">
      {outline.map((m) => (
        <div key={m.id} className="mb-3 last:mb-0">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{m.title}</p>
          {m.lessons.map((l) => {
            const Icon = done.has(l.id) ? CheckCircle2 : Circle;
            return (
              <Link key={l.id} to={`/learn/lessons/${l.id}`} data-testid={`outline-lesson-${l.id}`}
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${l.id === current ? "bg-[#e8eaf6] font-semibold text-[#222d99]" : "text-slate-700 hover:bg-slate-50"}`}>
                <Icon className={`h-3.5 w-3.5 shrink-0 ${done.has(l.id) ? "text-emerald-600" : "text-slate-300"}`} /> <span className="truncate">{l.title}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function LessonPlayer() {
  const { id } = useParams();
  const api = useApi();
  const { me } = useMe();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setD(null);
    setErr(null);
    api.get(`/lessons/${id}`).then((r) => setD(r.data)).catch((e) => setErr({ status: e?.response?.status, msg: errMsg(e) }));
  }, [api, id]);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  if (err) return (
    <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="lesson-locked">
      <Lock className="mx-auto h-8 w-8 text-slate-400" />
      <p className="mt-4 font-semibold text-slate-900">{err.msg}</p>
      <Button asChild variant="outline" className="mt-6"><Link to="/learn/courses">Back to courses</Link></Button>
    </div>
  );
  if (!d) return <Spinner />;
  const { lesson, course, progress } = d;
  const done = new Set(progress.completed_ids);
  const isDone = done.has(lesson.id);
  const isLearner = me.user.role === "learner";
  const canAct = isLearner && d.enrolled;
  const updateProgress = (p) => p && setD((prev) => ({ ...prev, progress: p }));

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/complete`);
      updateProgress(data);
      toast.success(data.percent === 100 ? "Course complete — well done!" : "Lesson completed");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const body = {
    text: <Markdown text={lesson.content} />,
    video: <VideoBody lesson={lesson} />,
    resource: <ResourceBody lesson={lesson} />,
    live: <LiveBody lesson={lesson} />,
    project: <ProjectBody lesson={lesson} />,
    discussion: <DiscussionBody lesson={lesson} canPost={d.enrolled} />,
    quiz: <QuizBody lesson={lesson} lastAttempt={d.last_attempt} canAnswer={canAct} onPassed={updateProgress} />,
  }[lesson.type];

  return (
    <div>
      <Link to={`/learn/courses/${course.id}`} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#222d99]" data-testid="lesson-back-to-course"><ArrowLeft className="h-4 w-4" /> {course.title}</Link>
      <div className="mb-6 flex items-center gap-3">
        <Progress value={progress.percent} className="h-1.5 max-w-xs" />
        <span className="text-xs text-slate-500" data-testid="lesson-course-progress">{progress.percent}% of course</span>
      </div>
      {!d.enrolled && <p className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="lesson-preview-banner">You're viewing a free preview. Enrol to track progress and take notes.</p>}
      <div className="grid gap-6 lg:grid-cols-3">
        <article className="rise rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <Badge variant="outline">{FORMAT_LABELS[lesson.type]}</Badge>
            <span className="text-xs text-slate-500">{lesson.duration_min} min</span>
            {!lesson.required && <Badge variant="secondary">Optional</Badge>}
          </div>
          <h1 className="mb-6 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="lesson-title">{lesson.title}</h1>
          {body}
          <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-6">
            {d.prev_id ? <Button asChild variant="ghost" data-testid="lesson-prev-btn"><Link to={`/learn/lessons/${d.prev_id}`}><ArrowLeft className="mr-1 h-4 w-4" /> Previous</Link></Button> : <span />}
            <div className="flex gap-2">
              {canAct && lesson.type !== "quiz" && (
                isDone ? <span className="flex items-center gap-1 text-sm font-semibold text-emerald-700" data-testid="lesson-completed-label"><CheckCircle2 className="h-4 w-4" /> Completed</span>
                  : <Button onClick={complete} disabled={busy} variant="outline" className="rounded-full border-[#222d99] text-[#222d99]" data-testid="lesson-complete-btn">
                      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Mark complete
                    </Button>
              )}
              {d.next_id && <Button asChild className="rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="lesson-next-btn"><Link to={`/learn/lessons/${d.next_id}`}>Next <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>}
            </div>
          </div>
        </article>
        <aside className="space-y-4">
          {d.enrolled && <Notes key={lesson.id} lessonId={lesson.id} initial={d.note} />}
          <Outline outline={d.outline} current={lesson.id} done={done} />
        </aside>
      </div>
    </div>
  );
}
